import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";

import { ENTITY_REGISTRATION_SOURCE, ENTITY_STATUS, PERMISSIONS } from "../../constants";
import { flagFor, splitE164 } from "../../phoneCountries";
import DialogConfirmation from "../generic/DialogConfirmation";
import {
  ENTITY_DOC_STATUS_COLOR,
  ENTITY_DOC_STATUS_LABELS,
  ENTITY_REGISTRATION_SOURCE_LABELS,
  ENTITY_REQUEST_ERROR_LABELS,
  ENTITY_STATUS_COLOR,
  ENTITY_STATUS_LABELS,
  ENTITY_TYPE_LABELS,
  formatDate,
} from "./entityLabels";
import { formatNit } from "./entityLogic";
import EntityContacts from "./EntityContacts";
import EntityDocuments from "./EntityDocuments";
import EntityForm from "./EntityForm";
import {
  addEntityContact,
  inviteEntityAdmin,
  listEntityMembers,
  memberErrorLabel,
  approveEntity,
  deleteEntity,
  deleteEntityContact,
  entityFilePublicUrl,
  fetchEntityAgreements,
  getEntity,
  getEntityLogs,
  rejectEntity,
  updateEntityContact,
} from "../../services/backend/EntityService";
import { getEntityConfig } from "../../services/backend/EntityConfigService";
import { removeEntity, setCurrentEntity } from "../../redux/slices/entitySlice";

const LOG_ACTION_LABELS = {
  CREATE: "Registró la entidad",
  UPDATE: "Editó la entidad",
  DELETE: "Eliminó la entidad",
  ADD_DOCUMENT: "Cargó un documento",
  REPLACE_DOCUMENT: "Reemplazó un documento",
  DELETE_DOCUMENT: "Quitó un documento",
  APPROVE: "Validó un documento",
  REJECT: "Rechazó un documento",
  APPROVE_ENTITY: "Aprobó la entidad",
  REJECT_ENTITY: "Rechazó la entidad",
  SET_LOGO: "Cambió el logo",
  ADD_CONTACT: "Agregó un contacto",
  UPDATE_CONTACT: "Editó un contacto",
  SET_PRIMARY_CONTACT: "Cambió el contacto principal",
  DELETE_CONTACT: "Quitó un contacto",
};

const Field = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2">{children ?? "—"}</Typography>
  </Grid>
);

export default function EntityView() {
  const { nit } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.value);

  const [entity, setEntity] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [bucket, setBucket] = useState("");
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [contactBusy, setContactBusy] = useState(false);
  const [accessCount, setAccessCount] = useState(null); // null = sin consultar
  // Las cuentas de la entidad. El panel de contactos las necesita para agrupar
  // cada correo y cada teléfono bajo la persona que responde por él.
  const [members, setMembers] = useState([]);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteNotice, setInviteNotice] = useState(null);

  const perms = user?.permissions || [];
  const roles = user?.roles || [];
  // ADMIN pasa por rol: entity.* no existe en las cuentas creadas antes de este
  // módulo (los permisos son una foto por usuario).
  const isAdmin = roles.includes("ADMIN");
  const canUpdate = perms.includes(PERMISSIONS.ENTITY.UPDATE) || isAdmin;
  const canDelete = perms.includes(PERMISSIONS.ENTITY.DELETE) || isAdmin;
  const canApprove = perms.includes(PERMISSIONS.ENTITY.APPROVE) || isAdmin;

  const load = useCallback(async () => {
    setLoading(true);
    // El catálogo y los convenios se piden junto con la entidad: los tres
    // alimentan la misma pantalla y en serie sólo suman esperas.
    const [found, cfg, agr] = await Promise.all([
      getEntity(nit),
      getEntityConfig(),
      fetchEntityAgreements(nit),
    ]);
    setEntity(found);
    setCatalog(cfg.entityDocumentCatalog || []);
    setBucket(cfg.storageBucket || "");
    setAgreements(agr.items || []);
    dispatch(setCurrentEntity(found));
    setLoading(false);
  }, [nit, dispatch]);

  useEffect(() => { load(); }, [load]);

  const openLogs = async () => {
    setLogsOpen(true);
    setLogsLoading(true);
    setLogs(await getEntityLogs(nit, user));
    setLogsLoading(false);
  };

  const handleDelete = async () => {
    const result = await deleteEntity(nit, user);
    if (!result.ok) {
      setActionError(
        ENTITY_REQUEST_ERROR_LABELS[result.error] || "No se pudo eliminar la entidad."
      );
      return;
    }
    dispatch(removeEntity(entity.nit));
    navigate("/entities");
  };

  const handleApprove = async () => {
    setApproving(true); setActionError(null);
    const result = await approveEntity(nit, user);
    setApproving(false);
    if (!result.ok) {
      setActionError(ENTITY_REQUEST_ERROR_LABELS[result.error] || "No se pudo aprobar la entidad.");
      return;
    }
    await load();
  };

  const handleReject = async () => {
    setApproving(true); setActionError(null);
    const result = await rejectEntity(nit, rejectReason.trim(), user);
    setApproving(false);
    if (!result.ok) {
      setActionError(ENTITY_REQUEST_ERROR_LABELS[result.error] || "No se pudo rechazar la entidad.");
      return;
    }
    setRejectOpen(false);
    setRejectReason("");
    await load();
  };

  // Los contactos se guardan de a uno, con su propia acción: cada cambio es una
  // escritura completa de la lista en el servidor, así que la ficha se recarga
  // en vez de adivinar el resultado.
  // Does this entity already have anyone with an account? Decides whether the
  // invite-administrator button is offered. Queried separately from the record
  // because staff live in `users`, not on the entity document.
  //
  // The list itself is kept, not just the count: the contact panel groups rows
  // by the person who owns them, so it needs the names. Read with entity.read,
  // so a manager without edit rights still sees who is who.
  useEffect(() => {
    if (!entity) return;
    (async () => {
      const result = await listEntityMembers(nit, user);
      if (!result.ok) return;
      const list = result.data?.members || [];
      setMembers(list);
      setAccessCount(list.filter((m) => !m.disabled).length);
    })();
  }, [entity, nit, user]);

  const handleInviteAdmin = async () => {
    const email = entity.email;
    if (!email) return;
    setInviteBusy(true);
    setActionError(null);
    setInviteNotice(null);
    const result = await inviteEntityAdmin({ nit, email }, user);
    setInviteBusy(false);
    if (!result.ok) {
      setActionError(memberErrorLabel(result.error));
      return;
    }
    setInviteNotice(`Invitación enviada a ${email}.`);
  };

  const contactAction = async (fn) => {
    setContactBusy(true); setActionError(null);
    const result = await fn();
    setContactBusy(false);
    if (!result.ok) {
      setActionError(ENTITY_REQUEST_ERROR_LABELS[result.error] || "No se pudo actualizar el contacto.");
      return;
    }
    setEntity((prev) => ({ ...prev, ...result.data }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!entity) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h6" gutterBottom>Entidad no encontrada</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          La entidad {nit} no existe o fue eliminada.
        </Typography>
        <Button onClick={() => navigate("/entities")} startIcon={<ArrowBackIcon />}>
          Volver a entidades
        </Button>
      </Container>
    );
  }

  const completion = entity.completionPercentage || 0;
  const logoUrl = entityFilePublicUrl(bucket, entity.logoPath);

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <IconButton onClick={() => navigate("/entities")}><ArrowBackIcon /></IconButton>
        {/* El logo va con `contain`: recortado por los bordes deja de ser el
            logo. Cuando no hay, la caja se queda igual para que la cabecera no
            salte al cargar. */}
        {/* Two elements, not one Box with a switching `component`: an <img> may
            not carry a `children` prop AT ALL, and `{!logo && <Icon/>}` still
            passes one — `false` — once the logo exists. React throws in
            assertValidProps and the whole screen dies, so this only ever broke
            for entities that HAVE a logo. */}
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt=""
            sx={{
              width: 72, height: 40, objectFit: "contain", borderRadius: 1, flexShrink: 0,
              bgcolor: "grey.100",
            }}
          />
        ) : (
          <Box
            sx={{
              width: 72, height: 40, borderRadius: 1, flexShrink: 0, bgcolor: "grey.100",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <BusinessIcon fontSize="small" color="disabled" />
          </Box>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h5" noWrap>{entity.legalName}</Typography>
          <Typography variant="caption" color="text.secondary">{formatNit(entity.nit)}</Typography>
        </Box>
        <Chip
          label={ENTITY_STATUS_LABELS[entity.status] || entity.status}
          color={ENTITY_STATUS_COLOR[entity.status] || "default"}
        />
        {entity.registrationSource === ENTITY_REGISTRATION_SOURCE.PUBLIC && (
          <Chip
            size="small"
            variant="outlined"
            label={ENTITY_REGISTRATION_SOURCE_LABELS[ENTITY_REGISTRATION_SOURCE.PUBLIC]}
          />
        )}
        {isAdmin && (
          <Tooltip title="Bitácora">
            <IconButton onClick={openLogs}><HistoryIcon /></IconButton>
          </Tooltip>
        )}
        {canUpdate && (
          <Button startIcon={<EditIcon />} onClick={() => setFormOpen(true)}>Editar</Button>
        )}
        {canDelete && (
          <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmOpen(true)}>
            Eliminar
          </Button>
        )}
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      {inviteNotice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInviteNotice(null)}>
          {inviteNotice}
        </Alert>
      )}

      {/* An entity with no account cannot maintain itself: every data or
          document change has to be requested elsewhere and typed in by a
          manager. Inviting the administrator is what makes it autonomous. Only
          shown when there is an address to send it to. */}
      {canUpdate && accessCount === 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={entity.email && (
            <Button
              size="small" startIcon={<PersonAddAltIcon />}
              disabled={inviteBusy} onClick={handleInviteAdmin}
              sx={{ textTransform: "none" }}
            >
              Invitar
            </Button>
          )}
        >
          {entity.email
            ? `Esta entidad no tiene acceso a la plataforma. Puedes invitar a ${entity.email} para que administre sus propios datos.`
            : "Esta entidad no tiene acceso a la plataforma ni un correo de contacto al que invitarla."}
        </Alert>
      )}

      {/* Cola de revisión: una entidad que se registró sola por el portal no
          puede recibir convenios hasta que alguien la mire. El aviso va arriba
          de todo porque es la única acción pendiente de esta pantalla. */}
      {entity.status === ENTITY_STATUS.PENDING && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            canApprove && (
              <Stack direction="row" spacing={1}>
                <Button size="small" color="inherit" disabled={approving}
                  onClick={() => setRejectOpen(true)}>
                  Rechazar
                </Button>
                <Button size="small" variant="contained" color="success" disabled={approving}
                  startIcon={<CheckCircleOutlineIcon />} onClick={handleApprove}>
                  Aprobar
                </Button>
              </Stack>
            )
          }
        >
          Esta entidad se registró por el portal público y aún no ha sido aprobada.
          Mientras siga pendiente no se le puede asociar un convenio.
        </Alert>
      )}

      {entity.status === ENTITY_STATUS.INACTIVE && entity.rejectionReason && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Motivo del rechazo:</strong> {entity.rejectionReason}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Field label="Naturaleza jurídica">{ENTITY_TYPE_LABELS[entity.entityType] || entity.entityType}</Field>
          <Field label="Nombre comercial">{entity.tradeName}</Field>
          <Field label="Sector">{entity.sector}</Field>
          <Field label="País">{entity.country}</Field>
          <Field label="Departamento">{entity.department}</Field>
          <Field label="Ciudad">{entity.city}</Field>
          <Field label="Dirección">{entity.address}</Field>
          <Field label="Teléfono">{entity.phone}</Field>
          <Field label="Correo">{entity.email}</Field>
          <Field label="Sitio web">{entity.website}</Field>
          <Field label="Registrada por">{entity.createdByName}</Field>
          <Field label="Convenios">{entity.agreementCount || 0}</Field>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Representante legal
        </Typography>
        <Grid container spacing={2}>
          <Field label="Nombre">{entity.legalRepName}</Field>
          <Field label="Tipo de documento">{entity.legalRepIdType}</Field>
          <Field label="Número de documento">{entity.legalRepIdNumber}</Field>
          <Field label="Correo">{entity.legalRepEmail}</Field>
          <Field label="Teléfono">
            {entity.legalRepPhone ? (
              <>
                {/* La bandera sale del país guardado y, para los números
                    anteriores al selector, del indicativo. */}
                <Typography component="span" sx={{ mr: 0.5 }}>
                  {flagFor(
                    entity.legalRepPhoneCountryCode ||
                      splitE164(entity.legalRepPhone).countryCode
                  )}
                </Typography>
                {entity.legalRepPhone}
              </>
            ) : null}
          </Field>
        </Grid>

        {entity.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" display="block">Observaciones</Typography>
            <Typography variant="body2">{entity.notes}</Typography>
          </>
        )}
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>Contactos</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Un contacto que agregues aquí queda <strong>sin verificar</strong>: tecleado por
          nosotros no prueba que exista. La entidad lo verifica desde su ficha del portal, y
          sólo entonces sirve para entrar y para recibir los avisos de vencimiento.
        </Typography>
        <EntityContacts
          emails={entity.contactEmails || []}
          phones={entity.contactPhones || []}
          members={members}
          country={entity.country}
          readOnly={!canUpdate}
          busy={contactBusy}
          onAdd={(channel, contact) =>
            contactAction(() => addEntityContact({ nit, channel, contact, user }))
          }
          onUpdate={(channel, contactId, contact) =>
            contactAction(() => updateEntityContact({ nit, channel, contactId, contact, user }))
          }
          onSetPrimary={(channel, contactId) =>
            contactAction(() => updateEntityContact({ nit, channel, contactId, setPrimary: true, user }))
          }
          onDelete={(channel, contactId) =>
            contactAction(() => deleteEntityContact({ nit, channel, contactId, user }))
          }
        />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Documentación</Typography>
          <Chip
            size="small"
            color={ENTITY_DOC_STATUS_COLOR[entity.documentationStatus] || "default"}
            label={ENTITY_DOC_STATUS_LABELS[entity.documentationStatus] || "—"}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, completion)}
            sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
          />
          <Typography variant="body2" sx={{ minWidth: 44, textAlign: "right" }}>{completion}%</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Lo que se le exige a esta entidad depende de su naturaleza jurídica; lo define la
          configuración del módulo de convenios.
        </Typography>

        <EntityDocuments
          entity={entity}
          catalog={catalog}
          // La función devuelve los documentos y los rollups ya recalculados, así
          // que la ficha se actualiza sin volver a leer el documento entero.
          onChange={(data) => setEntity((prev) => ({ ...prev, ...data }))}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Convenios</Typography>
        {agreements.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Esta entidad aún no tiene convenios registrados.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Objeto</TableCell>
                  <TableCell>Vigencia</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agreements.map((a) => (
                  <TableRow key={a.code} hover sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/agreements/${a.code}`)}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{a.code}</TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 420 }} title={a.subject}>
                        {a.subject}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatDate(a.startDate)} — {formatDate(a.endDate)}
                    </TableCell>
                    <TableCell>{a.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <EntityForm
        open={formOpen}
        entity={entity}
        onClose={() => setFormOpen(false)}
        onSaved={() => load()}
      />

      <DialogConfirmation
        confirmInfo={{
          open: confirmOpen,
          title: "Eliminar entidad",
          confirm: "Eliminar",
        }}
        content={
          entity.agreementCount > 0
            ? `${entity.legalName} tiene ${entity.agreementCount} convenio(s) registrados y no se puede eliminar. Si ya no se trabaja con ella, márcala como inactiva.`
            : `¿Eliminar a ${entity.legalName}? Dejará de aparecer en el listado, pero el documento se conserva.`
        }
        handleCloseDialog={(accepted) => {
          setConfirmOpen(false);
          if (accepted) handleDelete();
        }}
      />

      {/* Rechazar pide el motivo, como al rechazar un documento: la entidad lo
          ve en su ficha del portal, y sin él no sabría qué corregir. */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Rechazar el registro</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            La entidad quedará inactiva y verá este motivo cuando entre a su ficha.
          </Typography>
          <TextField
            autoFocus fullWidth multiline minRows={3}
            label="Motivo del rechazo"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            helperText="Mínimo 5 caracteres. Explica qué debe corregir."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={approving}>Cancelar</Button>
          <Button color="error" variant="contained"
            disabled={approving || rejectReason.trim().length < 5}
            onClick={handleReject}>
            Rechazar
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={logsOpen} onClose={() => setLogsOpen(false)}>
        <Box sx={{ width: { xs: 320, sm: 420 }, p: 2 }}>
          <Typography variant="h6" gutterBottom>Bitácora</Typography>
          {logsLoading && <CircularProgress size={22} />}
          {!logsLoading && logs.length === 0 && (
            <Typography variant="body2" color="text.secondary">Sin eventos registrados.</Typography>
          )}
          <List dense>
            {logs.map((log) => (
              <ListItem key={log.id} divider alignItems="flex-start">
                <ListItemText
                  primary={LOG_ACTION_LABELS[log.action] || log.action}
                  secondary={
                    <>
                      {log.userName}
                      {log.timestamp ? ` · ${new Date(log.timestamp).toLocaleString("es-CO")}` : ""}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Container>
  );
}
